import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef
} from 'react';

import { UpdateUserAPI } from '../../API/UserAPI';

import { UserContext, UserDispatchContext } from '../../Contexts/UserContext';

import PasswordDialog from './PasswordDialog';
import PhoneVerificationDialog from './PhoneVerificationDialog';
import { extractErrorMessage } from '../../Utils/Errors/Errors';
import { updateUser } from '../../Utils/User/Actions';
import { hasUserSsoCredentials } from '../../Enums/users';

const PhoneValidation = ({
  setAlert,
  phoneUpdate,
  setPhoneUpdate,
  phone,
  phoneCountryCode,
  setPhone,
  setErrors,
  setPhoneCountryCode,
  requestCallback = null,
  successCallback = null,
  otpChannels = null
}) => {
  // * CONTEXTS
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);

  // * STATE HOOKS
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [phoneVerificationDialog, setPhoneVerificationDialog] = useState(null);
  const [currentPassword, setCurrentPassword] = useState(null);
  const hasProcessedSubmit = useRef(false);

  // * FUNCTIONS

  const handlePasswordDialog = useCallback(
    isDialogOpen => {
      setPhoneUpdate({ submit: isDialogOpen });
      setPasswordDialog(isDialogOpen);
    },
    [setPasswordDialog, setPhoneUpdate]
  );

  const handleVerificationDialog = useCallback(
    async (isDialogOpen, wasSuccessful) => {
      setPhoneUpdate({
        submit: isDialogOpen,
        successfulPassword: wasSuccessful
      });
      setPhoneVerificationDialog(isDialogOpen);
    },
    [setPhoneVerificationDialog, setPhoneUpdate]
  );

  const onConfirmPhoneUpdate = useCallback(
    async modalPassword => {
      const body = {
        phoneCountryCode,
        phone,
        currentPassword: modalPassword
      };

      if (otpChannels) {
        body.otpChannels = {
          sms: Boolean(otpChannels.sms),
          whatsapp: Boolean(otpChannels.whatsapp)
        };
      }

      const response = await UpdateUserAPI(
        currentUser.token,
        currentUser.id,
        body
      );

      setCurrentPassword(modalPassword);
      return response;
    },
    [phoneCountryCode, phone, currentUser, otpChannels]
  );

  const onSuccessConfirmPhoneUpdate = useCallback(() => {
    setCurrentUser(user => ({
      ...user,
      phone,
      phoneCountryCode,
      phoneValidated: false
    }));
    handleVerificationDialog(true, true);
    if (setPhone && setPhoneCountryCode) {
      setPhone('');
      setPhoneCountryCode('');
    }
  }, [
    setCurrentUser,
    handleVerificationDialog,
    setPhone,
    setPhoneCountryCode,
    phone,
    phoneCountryCode
  ]);

  useEffect(() => {
    if (!phoneUpdate.submit || phoneUpdate.successfulPassword) {
      hasProcessedSubmit.current = false;
      return;
    }

    if (hasProcessedSubmit.current) {
      return;
    }

    hasProcessedSubmit.current = true;
    let ignoreRequest = false;

    const fetchData = async () => {
      if (ignoreRequest) {
        return;
      }

      const body = { phoneCountryCode, phone };
      if (otpChannels) {
        body.otpChannels = {
          sms: Boolean(otpChannels.sms),
          whatsapp: Boolean(otpChannels.whatsapp)
        };
      }

      const response = await UpdateUserAPI(
        currentUser.token,
        currentUser.id,
        body
      );

      if (!response.success) {
        setAlert({
          type: 'error',
          message: extractErrorMessage(response).message
        });
        hasProcessedSubmit.current = false;
        return;
      }
      const { token, appToken } = response.data.data;

      // Se pasa null a updateUser para que solo se actualice el token en Android y iOS
      updateUser(null, token, appToken);
      setPhoneVerificationDialog(true);
    };

    let modalTitle = 'Validar';
    if (!currentUser.phone) {
      modalTitle = 'Agregar';
    } else if (currentUser.phoneValidated) {
      modalTitle = 'Actualizar';
    }
    if (hasUserSsoCredentials(currentUser)) {
      fetchData();
    } else if (currentUser.hasPassword) {
      setPasswordDialog({
        title: `${modalTitle} número celular`,
        requestCallback: onConfirmPhoneUpdate,
        successCallback: onSuccessConfirmPhoneUpdate
      });
    } else {
      setAlert({
        type: 'error',
        message:
          'Debes configurar una contraseña o vincular una red social para continuar'
      });
      setPhoneUpdate({ submit: false });
    }
    return () => {
      ignoreRequest = true;
    };
  }, [
    phoneUpdate.submit,
    phoneUpdate.successfulPassword,
    currentUser,
    setCurrentUser,
    onConfirmPhoneUpdate,
    onSuccessConfirmPhoneUpdate,
    phone,
    phoneCountryCode,
    setAlert,
    setPhoneUpdate,
    otpChannels
  ]);

  if (!currentUser || !phoneUpdate.submit) {
    return <></>;
  }

  return (
    <>
      {passwordDialog && (
        <PasswordDialog
          title={passwordDialog.title}
          open={Boolean(passwordDialog)}
          setDialog={handlePasswordDialog}
          setErrors={setErrors}
          setAlert={setAlert}
          requestCallback={passwordDialog.requestCallback}
          successCallback={passwordDialog.successCallback}
        />
      )}
      {phoneVerificationDialog && (
        <PhoneVerificationDialog
          title="Validar número celular"
          open={phoneVerificationDialog}
          formPhone={phone}
          formPhoneCountryCode={phoneCountryCode}
          setDialog={handleVerificationDialog}
          requestCallback={requestCallback}
          successCallback={successCallback}
          setAlert={setAlert}
          currentPassword={currentPassword}
          otpChannels={otpChannels}
        />
      )}
    </>
  );
};

export default PhoneValidation;
